export function ConsistentIncomeStream() {
  return (
    <section className="py-20 px-4 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <div className="inline-block bg-teal-100 text-teal-800 px-6 py-2 rounded-full font-bold text-sm mb-4">
            BENEFIT #1
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Consistent, High-Interest Passive Income
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Transform your apartment building sale into a reliable income stream that generates monthly cash flow for years or decades
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">
              Monthly Payments Instead of a Lump Sum
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Instead of receiving a one-time lump sum payment at closing, seller financing allows you to receive <strong>consistent monthly payments</strong> that include both principal and interest. This creates a predictable, steady stream of passive income that can last for 5, 10, 15, or even 30 years, depending on the loan term you negotiate.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              The interest component of these payments represents pure profit above and beyond your original property value. With seller financing interest rates typically ranging from <strong>6% to 10% or higher</strong>, you can earn substantially more than you would from traditional investments like bonds, CDs, or savings accounts.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              This passive income stream requires minimal effort on your part once the loan is established. Unlike managing rental properties, you don't deal with tenants, maintenance, or property management headaches. You simply collect your monthly payment and enjoy the consistent cash flow.
            </p>

            <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-md">
              <h4 className="text-xl font-bold text-gray-900 mb-3">Real-World Example</h4>
              <p className="text-gray-700 mb-2">
                <strong>Sale Price:</strong> $2,000,000
              </p>
              <p className="text-gray-700 mb-2">
                <strong>Down Payment (20%):</strong> $400,000
              </p>
              <p className="text-gray-700 mb-2">
                <strong>Financed Amount:</strong> $1,600,000
              </p>
              <p className="text-gray-700 mb-2">
                <strong>Interest Rate:</strong> 7%
              </p>
              <p className="text-gray-700 mb-4">
                <strong>Loan Term:</strong> 15 years
              </p>
              <p className="text-xl font-bold text-teal-600">
                Monthly Payment: $14,382
              </p>
              <p className="text-lg font-bold text-gray-900 mt-2">
                Total Interest Earned: $988,760
              </p>
            </div>
          </div>

          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/4f4e3108177c23071afcfe3621d99b3e.jpg"
              alt="Consistent passive income growth chart from seller financing"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg mb-8"
            />

            <div className="bg-gray-50 rounded-lg p-6">
              <h4 className="text-2xl font-bold text-gray-900 mb-4">
                Key Advantages of Passive Income
              </h4>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">
                    <strong>Predictable Cash Flow:</strong> Know exactly how much you'll receive each month for budgeting and planning
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">
                    <strong>Higher Returns:</strong> Earn 6-10%+ interest compared to 1-3% from traditional savings vehicles
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">
                    <strong>Minimal Effort:</strong> No property management, tenant issues, or maintenance responsibilities
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">
                    <strong>Retirement Income:</strong> Perfect for sellers looking to create steady retirement income
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">
                    <strong>Secured by Real Estate:</strong> Your loan is backed by the property itself, providing security
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">
                    <strong>Inflation Protection:</strong> Fixed-rate loans mean your payment amount stays constant while inflation erodes the real value of the debt
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-lg p-8 md:p-12 text-white">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-5xl font-bold mb-2">6-10%</div>
              <div className="text-xl text-teal-100">Typical Interest Rate</div>
              <p className="text-sm text-teal-100 mt-2">
                Far exceeding traditional investment returns
              </p>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">$14K+</div>
              <div className="text-xl text-teal-100">Monthly Payment Example</div>
              <p className="text-sm text-teal-100 mt-2">
                On a $1.6M financed amount at 7%
              </p>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">$988K+</div>
              <div className="text-xl text-teal-100">Total Interest Earned</div>
              <p className="text-sm text-teal-100 mt-2">
                Over 15 years on the example above
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}